import { ForwardedRef, forwardRef, SelectHTMLAttributes, useMemo } from 'react';
import { BaseFormState } from '~/types/BaseFormState';
import { FormState } from 'react-hook-form';
import styles from './Select.module.css';
import classNames from 'classnames';

export type OptionTuple = [string, string | number | null | undefined] | [string, string | number | null | undefined, boolean];

export type Props<TFormState extends BaseFormState> = Pick<
  SelectHTMLAttributes<HTMLSelectElement>,
  'onChange' | 'onBlur' | 'defaultValue' | 'value' | 'name' | 'id' | 'required' | 'disabled' | 'aria-label'
> & {
  formState?: FormState<TFormState>;
  placeholder?: string | undefined;
  options: OptionTuple[];
};

function SelectElement<TFormState extends BaseFormState>(
  props: Props<TFormState>,
  ref: ForwardedRef<HTMLSelectElement>
) {
  const { options: optionsProp, name, formState, placeholder, value, defaultValue, ...rest } = props;

  const options = useMemo<OptionTuple[]>(() => {
    if (!placeholder) {
      return optionsProp;
    }

    return [[placeholder], ...optionsProp] as OptionTuple[];
  }, [optionsProp, placeholder]);

  return (
    <select
      className={classNames(styles.select, {
        [styles.error]: name && !!formState?.errors[name]?.message,
      })}
      value={value ?? defaultValue}
      name={name}
      ref={ref}
      {...rest}
    >
      {options.map(([key, value, disabled = false], index) => (
        <option key={`value-${index}`} value={value ?? ''} disabled={disabled}>
          {key}
        </option>
      ))}
    </select>
  );
}

export const Select = forwardRef(SelectElement) as <TFormState extends BaseFormState>(
  props: Props<TFormState> & { ref?: React.ForwardedRef<HTMLSelectElement> }
) => ReturnType<typeof SelectElement>;
